import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  reason?: string;
  /** l'intestazione sotto cui sta la voce (es. l'organizzazione di un repository): le voci
   * dello stesso gruppo vanno date una dopo l'altra */
  group?: string;
  /** il testo nel campo chiuso, se diverso dalla voce (es. `Labs/PopTown` per la voce `PopTown`) */
  selectedLabel?: string;
}

const ITEM_H = 40;
const HEAD_H = 30;
const SEARCH_H = 48;
const MAX_ITEMS = 8;
/** oltre questo numero di voci il menu ha il campo di ricerca (richiesta del 2026-11-01) */
const SEARCH_FROM = 10;

/** minuscolo e senza accenti: «città» si trova scrivendo «citta» */
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// La select della plancia: mai il `<select>` nativo (regola del design system). Accessibile
// come quella nativa: combobox + listbox, frecce, Home/End, Invio/Spazio, Esc, ricerca per
// lettera, clic fuori chiude. Il menu sta in un portale, quindi un contenitore con
// `overflow: hidden` non lo taglia, e si apre sopra se sotto non c'è spazio. Con più di
// SEARCH_FROM voci il menu si apre con un campo di ricerca, che filtra anche per gruppo.
export function Select({
  value,
  onChange,
  options,
  width,
  label,
  searchable,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  width?: number | string;
  label?: string;
  /** forza il campo di ricerca acceso o spento; di partenza si accende con molte voci */
  searchable?: boolean;
}) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<{ left: number; top: number; width: number }>();
  const typed = useRef({ text: '', at: 0 });
  const current = options.find((o) => o.value === value);
  const withSearch = searchable ?? options.length > SEARCH_FROM;

  // le voci visibili: tutte, o quelle che contengono il testo cercato (nella voce o nel gruppo)
  const filter = (text: string) => {
    const q = fold(text.trim());
    return q ? options.filter((o) => fold(`${o.group ?? ''} ${o.label}`).includes(q)) : options;
  };
  const shown = filter(query);
  const groups = shown.filter((o, i) => o.group && o.group !== shown[i - 1]?.group).length;

  const enabled = (i: number) => !!shown[i] && !shown[i].disabled;
  const step = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < shown.length; i += dir) if (enabled(i)) return i;
    return from;
  };

  const openMenu = () => {
    setQuery('');
    const i = options.findIndex((o) => o.value === value);
    setActive(i >= 0 && !options[i].disabled ? i : options.findIndex((o) => !o.disabled));
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    setQuery('');
    if (refocus) button.current?.focus();
  };
  const choose = (i: number) => {
    if (!enabled(i)) return;
    onChange(shown[i].value);
    close();
  };

  // posizione del menu: sotto il campo, o sopra se sotto non ci sta
  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const r = button.current.getBoundingClientRect();
    const height = Math.min(options.length, MAX_ITEMS) * ITEM_H + 8 + (withSearch ? SEARCH_H : 0);
    const up = r.bottom + height > window.innerHeight && r.top > height;
    setPlace({ left: r.left, top: up ? r.top - height - 4 : r.bottom + 4, width: r.width });
  }, [open, options.length, withSearch]);

  // con la ricerca il fuoco va nel campo, appena il menu c'è
  useEffect(() => {
    if (open && withSearch && place) search.current?.focus();
  }, [open, withSearch, place]);

  // clic fuori, scroll e ridimensionamento chiudono
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!button.current?.contains(t) && !popup.current?.contains(t)) close(false);
    };
    const away = (e: Event) => {
      if (!popup.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', outside);
    window.addEventListener('resize', away);
    window.addEventListener('scroll', away, true);
    return () => {
      document.removeEventListener('mousedown', outside);
      window.removeEventListener('resize', away);
      window.removeEventListener('scroll', away, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) document.getElementById(`${id}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);

  const onKey = (e: KeyboardEvent, inSearch = false) => {
    const keys: Record<string, () => void> = {
      ArrowDown: () => (open ? setActive((a) => step(a, 1)) : openMenu()),
      ArrowUp: () => (open ? setActive((a) => step(a, -1)) : openMenu()),
      Enter: () => (open ? choose(active) : openMenu()),
      Escape: () => open && close(),
      Tab: () => open && close(false),
    };
    // nel campo di ricerca Home, End e lo spazio servono a scrivere
    if (!inSearch) {
      keys.Home = () => open && setActive(step(-1, 1));
      keys.End = () => open && setActive(step(shown.length, -1));
      keys[' '] = () => (open ? choose(active) : openMenu());
    }
    const run = keys[e.key];
    if (run) {
      // Esc a menu chiuso non è suo: passa al modale che lo contiene
      if (e.key === 'Escape' && !open) return;
      if (e.key !== 'Tab') e.preventDefault();
      run();
      return;
    }
    if (inSearch) return;
    // digitazione: con la ricerca apre il menu e scrive lì; senza, salta alla prima voce così
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
      if (withSearch) {
        e.preventDefault();
        if (!open) openMenu();
        setQuery(e.key);
        return;
      }
      const now = Date.now();
      typed.current = {
        text: (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLowerCase(),
        at: now,
      };
      const i = options.findIndex(
        (o) => !o.disabled && o.label.toLowerCase().startsWith(typed.current.text),
      );
      if (i >= 0) {
        if (!open) openMenu();
        setActive(i);
      }
    }
  };

  const listHeight = Math.min(shown.length + groups * (HEAD_H / ITEM_H), MAX_ITEMS) * ITEM_H + 8;

  return (
    <>
      <button
        ref={button}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={open && !withSearch ? `${id}-${active}` : undefined}
        aria-label={label}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => onKey(e)}
        style={{
          width: width ?? '100%',
          height: 34,
          padding: '0 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderRadius: 'var(--r-sm)',
          background: 'var(--panel)',
          color: 'var(--text)',
          border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
          boxShadow: open ? '0 0 0 3px var(--accent-soft)' : 'none',
          font: '400 14px/20px var(--font-ui)',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span className="trunc" style={{ flex: 1, minWidth: 0 }}>
          {current?.selectedLabel ?? current?.label ?? ''}
        </span>
        <Icon name="chevron-down" size={14} color="var(--dim)" />
      </button>
      {open &&
        place &&
        createPortal(
          <div
            ref={popup}
            style={{
              position: 'fixed',
              left: place.left,
              top: place.top,
              minWidth: place.width,
              maxWidth: 'calc(100vw - 16px)',
              zIndex: 500,
              background: 'var(--panel)',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--r-md)',
              boxShadow: 'var(--shadow-2)',
              overflow: 'hidden',
            }}
          >
            {withSearch && (
              <div style={{ padding: '6px 6px 2px', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 16, top: 17, display: 'grid' }}>
                  <Icon name="search" size={14} color="var(--dim)" />
                </span>
                <input
                  ref={search}
                  type="text"
                  role="combobox"
                  aria-expanded
                  aria-controls={`${id}-list`}
                  aria-activedescendant={shown.length ? `${id}-${active}` : undefined}
                  aria-label={`Cerca ${label ?? ''}`.trim()}
                  placeholder="Cerca…"
                  value={query}
                  onChange={(e) => {
                    // a ogni ricerca la voce attiva è la prima che si può scegliere
                    const found = filter(e.target.value);
                    setQuery(e.target.value);
                    setActive(
                      Math.max(
                        0,
                        found.findIndex((o) => !o.disabled),
                      ),
                    );
                  }}
                  onKeyDown={(e) => onKey(e, true)}
                  style={{
                    width: '100%',
                    height: 36,
                    padding: '0 10px 0 32px',
                    borderRadius: 'var(--r-sm)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    font: '400 14px/20px var(--font-ui)',
                    outline: 'none',
                  }}
                />
              </div>
            )}
            <ul
              ref={list}
              id={`${id}-list`}
              role="listbox"
              aria-label={label}
              style={{
                maxHeight: listHeight,
                overflowY: 'auto',
                margin: 0,
                padding: 4,
                listStyle: 'none',
              }}
            >
              {shown.length === 0 && (
                <li
                  role="presentation"
                  style={{
                    padding: '10px',
                    color: 'var(--dim)',
                    font: '400 14px/20px var(--font-ui)',
                  }}
                >
                  Nessuna voce con «{query}»
                </li>
              )}
              {shown.map((o, i) => {
                const selected = o.value === value;
                const head = o.group && o.group !== shown[i - 1]?.group;
                return (
                  <li key={o.value} role="presentation" style={{ display: 'contents' }}>
                    {head && (
                      <div
                        aria-hidden="true"
                        style={{
                          height: HEAD_H,
                          padding: '10px 10px 2px',
                          color: 'var(--dim)',
                          font: '700 11px/16px var(--font-ui)',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {o.group}
                      </div>
                    )}
                    <div
                      id={`${id}-${i}`}
                      role="option"
                      aria-selected={selected}
                      aria-disabled={o.disabled || undefined}
                      title={o.reason}
                      onMouseEnter={() => enabled(i) && setActive(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => choose(i)}
                      style={{
                        minHeight: ITEM_H,
                        padding: o.group ? '0 10px 0 18px' : '0 10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        borderRadius: 'var(--r-sm)',
                        cursor: o.disabled ? 'not-allowed' : 'pointer',
                        color: o.disabled ? 'var(--faint)' : 'var(--text)',
                        background: i === active ? 'var(--bg-2)' : 'transparent',
                        font: `${selected ? 500 : 400} 14px/20px var(--font-ui)`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ flex: 1 }}>{o.label}</span>
                      {selected && <Icon name="check" size={14} color="var(--accent)" />}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}
